<template>
  <div class="modern-home page-theme page-theme--coral">
    <page-stripes side="left" :colors="stripeColors" :widths="stripeWidths" />
    <section class="hero">
      <div class="hero__content container">
        <p class="eyebrow">KOKONO SUITE</p>
        <h1>
          未来の業務フローを設計する
          <span>UI仕様書・画像コラボ・履歴書ビルダー</span>
        </h1>
        <p class="lead">
          kokono
          はプロダクト開発から採用までを一つの体験で結びます。チームの創造性を邪魔しないモダンな
          SaaS スイートで、要件定義・フィードバック・人材アピールを一気通貫に。
        </p>
        <div class="hero__actions">
          <a
            class="btn primary"
            href="https://www.kokono.cloud/"
            target="_blank"
            rel="noopener"
          >
            すぐに体験する
          </a>
          <a
            class="btn ghost"
            href="https://youtu.be/WBppSF03yS8?si=FkOaC8u9-LPUQiDj"
            target="_blank"
            rel="noopener"
          >
            デモを見る
          </a>
        </div>
        <div class="hero__stats">
          <div class="stat" v-for="item in stats" :key="item.label">
            <p class="value">{{ item.value }}</p>
            <p class="label">{{ item.label }}</p>
          </div>
        </div>
      </div>
      <video :muted="isMuted" autoplay loop playsinline class="hero__bg">
        <source
          src="https://app.kokono.cloud/assets/kokono_tutorial-CobmF_er.webm"
          type="video/webm"
        />
      </video>
      <button
        @click="toggleMute"
        class="video-mute-toggle"
        :aria-label="isMuted ? '音声をON' : '音声をOFF'"
      >
        <svg
          v-if="isMuted"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
          <line x1="23" y1="9" x2="17" y2="15"></line>
          <line x1="17" y1="9" x2="23" y2="15"></line>
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
          <path
            d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"
          ></path>
        </svg>
      </button>
      <div class="hero__glow"></div>
    </section>

    <section class="section container">
      <p class="eyebrow">COMPANY SNAPSHOT</p>
      <div class="timeline">
        <article
          class="timeline__card"
          v-for="item in timeline"
          :key="item.title"
        >
          <p class="title">{{ item.title }}</p>
          <p class="desc">{{ item.desc }}</p>
        </article>
      </div>
    </section>

    <section class="section container">
      <div class="section__header">
        <p class="eyebrow">KOKONO SERVICES</p>
        <h2>プロダクト群を横断する体験を実現</h2>
        <p>
          UI仕様書エディター / 画像コメント SaaS /
          履歴書ビルダーを一つのトーンで提供。目的別のサービスに移動する前に、ここで概要を掴めます。
        </p>
      </div>
      <div class="cards-grid">
        <article
          class="cards-grid__item"
          v-for="service in serviceCards"
          :key="service.name"
        >
          <div class="icon">
            <img
              v-if="service.iconType === 'image'"
              :src="service.icon"
              :alt="service.name"
            />
            <span v-else>{{ service.icon }}</span>
          </div>
          <h3>{{ service.name }}</h3>
          <p>{{ service.desc }}</p>
          <ul>
            <li v-for="point in service.points" :key="point">{{ point }}</li>
          </ul>
          <router-link class="inline-link" :to="service.link">
            詳細を見る
          </router-link>
        </article>
      </div>
    </section>

    <section class="section section--dark">
      <div class="container">
        <div class="section__header">
          <p class="eyebrow">CUSTOMER STORIES</p>
          <h2>プロダクト開発と採用を推進するチームの声</h2>
        </div>
        <div class="testimonials">
          <article
            class="testimonial"
            v-for="item in testimonials"
            :key="item.name"
          >
            <p class="quote">“{{ item.quote }}”</p>
            <p class="author">{{ item.name }}</p>
            <p class="role">{{ item.role }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section container">
      <div class="section__header">
        <p class="eyebrow">PARTNERS / LOGOS</p>
        <h2>業界を越えて使われる kokono</h2>
      </div>
      <div class="logos">
        <span v-for="logo in partners" :key="logo">{{ logo }}</span>
      </div>
    </section>

    <section class="section container final-cta" id="final-cta">
      <div>
        <p class="eyebrow">START TODAY</p>
        <h2>新サービスページを先行公開中</h2>
        <p>
          “next” ルートにて新しい UI を確認いただけます。十分に検証してから本番
          `/` へ切り替え可能です。
        </p>
      </div>
      <router-link class="btn primary" to="/next-services">
        新 Services ページ
      </router-link>
    </section>
  </div>
</template>

<script>
import { ref } from "vue";
import PageStripes from "../components/PageStripes.vue";

export default {
  name: "ModernHome",
  components: {
    PageStripes,
  },
  setup() {
    const stripeColors = ["#ffcdd2", "#ffabab", "#ff8a8a", "#ff5858"];
    const stripeWidths = [10, 7, 5, 2];
    const isMuted = ref(true);

    const toggleMute = () => {
      isMuted.value = !isMuted.value;
    };

    const stats = ref([
      { value: "15+", label: "プロジェクト導入" },
      { value: "4x", label: "仕様作成スピード" },
      { value: "99.9%", label: "クラウド稼働率" },
    ]);

    const timeline = ref([
      { title: "ミッション", desc: "UIとコミュニケーションの摩擦を取り除く" },
      { title: "設立", desc: "2008年12月 / 東京" },
      { title: "主な提供領域", desc: "UI設計 / フィードバック / キャリア" },
      { title: "最近の成果", desc: "kokono 製品スイートのベータ公開" },
    ]);

    const serviceCards = ref([
      {
        name: "kokono pro",
        icon: "/image/proIcon.png",
        iconType: "image",
        desc: "UI仕様書をノーコードで構築。ケース別テンプレとベストプラクティスを収録。",
        points: ["楽に速く作成", "多言語チーム対応", "AI連携"],
        link: "/next-services#pro",
      },
      {
        name: "kokono lite",
        icon: "/image/liteIcon.png",
        iconType: "image",
        desc: "画像に直接コメントを残せる軽量ツール。外部ゲスト共有にも最適。",
        points: ["簡単な操作", "手軽なURL共有", "ここの場所、を選ぶだけ"],
        link: "/next-services#lite",
      },
      {
        name: "kokono cv",
        icon: "/image/cvIcon.png",
        iconType: "image",
        desc: "履歴書・職務経歴書をブラウザで高速に作成。ATS互換レイアウトを自動生成。",
        points: ["らくらく作成", "AIサポート", "スキル棚卸し"],
        link: "/next-services#cv",
      },
    ]);

    const testimonials = ref([
      {
        quote:
          "kokono pro で仕様レビュー時間が半減。クライアントも即座に理解できる。",
        name: "プロダクトマネージャー / A社",
        role: "Product Lead",
      },
      {
        quote:
          "クリエイティブの差し戻しが激減。kokono lite の直感的なコメントで意思決定が早い。",
        name: "デザインディレクター / B社",
        role: "Design Director",
      },
      {
        quote:
          "kokono cv のテンプレで海外人材にも一貫したフォーマットを提供できた。",
        name: "HRBP / C社",
        role: "Talent Partner",
      },
    ]);

    const partners = ref([
      "NEO DX",
      "FAST OPS",
      "URBAN TECH",
      "CREATIVE EDGE",
      "MEETUP HR",
      "GLOBAL STUDIO",
    ]);

    return {
      stripeColors,
      stripeWidths,
      isMuted,
      toggleMute,
      stats,
      timeline,
      serviceCards,
      testimonials,
      partners,
    };
  },
};
</script>

<style lang="scss" scoped>
.modern-home {
  background: #ffffff;
  color: #1e293b;
  min-height: 100vh;
  padding-bottom: 4rem;
  position: relative;
  z-index: 1;
}

.container {
  width: min(1200px, 90vw);
  margin: 0 auto;
}

.hero {
  position: relative;
  overflow: hidden;
  padding: 7rem 0;
  text-align: left;
}

.hero__content {
  position: relative;
  z-index: 2;
}

.hero__bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.15;
}

.video-mute-toggle {
  position: absolute;
  top: 4rem;
  right: 2rem;
  z-index: 3;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #475569;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.video-mute-toggle svg {
  width: 20px;
  height: 20px;
}

.video-mute-toggle:hover {
  border-color: var(--theme-accent);
  color: var(--theme-accent);
  transform: scale(1.1);
}

.hero__glow {
  position: absolute;
  inset: 10% 15%;
  background: radial-gradient(circle, var(--theme-glow), transparent 70%);
  filter: blur(60px);
}

.eyebrow {
  letter-spacing: 0.3em;
  font-size: 0.8rem;
  color: var(--theme-accent);
}

h1 {
  font-size: clamp(2.5rem, 4vw, 4rem);
  line-height: 1.1;
  margin: 1rem 0;
  color: #1e293b;
}

h1 span {
  display: block;
  color: #64748b;
}

.lead {
  color: #64748b;
  max-width: 620px;
  line-height: 1.7;
}

.hero__actions {
  margin-top: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

.btn {
  border-radius: 999px;
  padding: 0.9rem 1.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn.primary {
  background: linear-gradient(
    120deg,
    var(--theme-accent-light),
    var(--theme-accent)
  );
  color: var(--theme-btn-text);
  box-shadow: 0 4px 12px var(--theme-glow);
}

.btn.ghost {
  border: 1px solid var(--theme-accent-pale);
  color: #475569;
  background: #ffffff;
}

.btn:hover {
  transform: translateY(-2px);
}

.hero__stats {
  margin-top: 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
}

.stat {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--theme-accent-light);
  padding: 1rem;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.value {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
}

.label {
  color: #64748b;
}

.section {
  padding: 5rem 0;
}

.section__header {
  max-width: 760px;
  margin: 0 auto 2rem;
  text-align: center;
}

.section__header h2 {
  color: #1e293b;
}

.section__header p {
  color: #64748b;
}

.timeline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.2rem;
}

.timeline__card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-top: 3px solid var(--theme-accent-pale);
  padding: 1.5rem;
  border-radius: 20px;
  min-height: 160px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.timeline__card .title {
  color: #1e293b;
  font-weight: 600;
}

.timeline__card .desc {
  color: #64748b;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
}

.cards-grid__item {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--theme-accent-light);
  border-radius: 20px;
  padding: 1.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.06);
}

.cards-grid__item h3 {
  color: #1e293b;
}

.cards-grid__item p {
  color: #64748b;
}

.cards-grid__item .icon {
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cards-grid__item .icon img {
  width: 2rem;
  height: 2rem;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
  background: #ffffff;
}

.cards-grid__item ul {
  list-style: none;
  padding-left: 1.2rem;
  color: #64748b;
}

.inline-link {
  margin-top: auto;
  font-weight: 600;
  color: var(--theme-accent);
}

.section--dark {
  background: var(--theme-bg-alt);
  border-top: 1px solid var(--theme-accent-pale);
  border-bottom: 1px solid var(--theme-accent-pale);
}

.testimonials {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.2rem;
}

.testimonial {
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.quote {
  font-size: 1.05rem;
  line-height: 1.6;
  color: #1e293b;
}

.author {
  color: #1e293b;
  font-weight: 600;
}

.role {
  color: #64748b;
}

.logos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  color: #94a3b8;
}

.final-cta {
  display: flex;
  justify-content: center;
  gap: 2rem;
  align-items: center;
  flex-wrap: wrap;
  flex-direction: column;
  background: var(--theme-bg-alt);
  border: 1px solid var(--theme-accent-pale);
  border-radius: 24px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  min-height: 120px;
  position: relative;
  z-index: 1;
  text-align: center;
}

.final-cta p {
  color: #64748b;
}

@media (max-width: 768px) {
  .hero {
    text-align: left;
  }

  .final-cta {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
